import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { ScrollText, Plus, Trash2, Save, RefreshCw, Eye } from 'lucide-react';
import CashoutRulesPopup from '../player/CashoutRulesPopup';

/**
 * Admin → Cashout Rules editor (Iter 142.10)
 * ==========================================
 * Lets ops edit the global cashout-rules document players see when they
 * tap "Cashout Rules" on any game card.
 *
 * Schema enforced server-side:
 *   { headline, intro, footer, rules: [{ icon, title, body }] }  (max 12 rules)
 *
 * Live preview pane uses the SAME CashoutRulesPopup component the
 * player sees, so what admins author is exactly what players see.
 */
const EMPTY_RULE = { icon: '✨', title: '', body: '' };

export default function CashoutRulesAdminPage() {
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/cashout-rules');
      setDoc(r.data);
      setDirty(false);
    } catch {
      toast.error('Could not load cashout rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (key, value) => {
    setDoc((d) => ({ ...(d || {}), [key]: value }));
    setDirty(true);
  };

  const setRule = (idx, key, value) => {
    setDoc((d) => {
      const next = { ...d, rules: [...(d.rules || [])] };
      next.rules[idx] = { ...next.rules[idx], [key]: value };
      return next;
    });
    setDirty(true);
  };

  const addRule = () => {
    setDoc((d) => ({ ...d, rules: [...(d?.rules || []), { ...EMPTY_RULE }] }));
    setDirty(true);
  };

  const removeRule = (idx) => {
    setDoc((d) => ({ ...d, rules: (d?.rules || []).filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const save = async () => {
    if (!doc) return;
    // Client-side validation — match server-side schema constraints
    if (!doc.headline || !doc.headline.trim()) { toast.error('Headline is required'); return; }
    if ((doc.rules || []).length === 0) { toast.error('Add at least one rule'); return; }
    if ((doc.rules || []).length > 12) { toast.error('Maximum 12 rules allowed'); return; }
    for (let i = 0; i < doc.rules.length; i++) {
      const r = doc.rules[i];
      if (!r.title?.trim() || !r.body?.trim()) {
        toast.error(`Rule ${i + 1}: title and body are required`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put('/api/admin/cashout-rules', {
        headline: doc.headline,
        intro: doc.intro || '',
        footer: doc.footer || '',
        rules: doc.rules,
      });
      toast.success('Cashout rules saved');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="spinner w-7 h-7" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="cashout-rules-admin-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
          >
            <ScrollText className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Cashout Rules</h1>
            <p className="text-sm text-gray-400 max-w-md">
              The rules players read when they tap the "Cashout Rules" button on any game card.
              Keep them short, clear, and friendly.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => setPreview(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 flex items-center gap-1.5"
            data-testid="cashout-rules-preview-btn"
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
          <button
            onClick={load}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="cashout-rules-reload-btn"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
        </div>
      </div>

      {/* Headline + intro */}
      <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
            Headline
          </label>
          <input
            value={doc?.headline || ''}
            onChange={(e) => setField('headline', e.target.value)}
            placeholder="e.g. How Cashouts Work"
            maxLength={80}
            className="w-full px-3 py-2.5 rounded-lg text-base text-white"
            style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="cashout-rules-headline-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
            Intro (one line)
          </label>
          <input
            value={doc?.intro || ''}
            onChange={(e) => setField('intro', e.target.value)}
            placeholder="e.g. Three simple rules. Read once, play freely."
            maxLength={400}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white"
            style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="cashout-rules-intro-input"
          />
        </div>
      </div>

      {/* Rules list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-white">Rules ({(doc?.rules || []).length}/12)</h2>
          <button
            onClick={addRule}
            disabled={(doc?.rules || []).length >= 12}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cashout-rules-add-btn"
          >
            <Plus className="w-4 h-4" /> Add rule
          </button>
        </div>

        {(doc?.rules || []).map((r, idx) => (
          <div
            key={idx}
            className="rounded-2xl p-4 space-y-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid={`cashout-rule-row-${idx}`}
          >
            <div className="flex items-start gap-3">
              <input
                value={r.icon || ''}
                onChange={(e) => setRule(idx, 'icon', e.target.value)}
                placeholder="🎯"
                maxLength={4}
                className="w-14 px-2 py-2 rounded-lg text-2xl text-center"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid={`cashout-rule-icon-${idx}`}
              />
              <input
                value={r.title || ''}
                onChange={(e) => setRule(idx, 'title', e.target.value)}
                placeholder="Rule title (e.g. Minimum Cashout: 3× your last load)"
                maxLength={80}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg text-base font-bold text-white"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid={`cashout-rule-title-${idx}`}
              />
              <button
                onClick={() => removeRule(idx)}
                className="p-2.5 rounded-lg hover:bg-red-500/20 transition shrink-0"
                title="Remove rule"
                data-testid={`cashout-rule-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
            <textarea
              value={r.body || ''}
              onChange={(e) => setRule(idx, 'body', e.target.value)}
              placeholder="Rule explanation — one or two short sentences."
              maxLength={400}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-300 resize-y"
              style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid={`cashout-rule-body-${idx}`}
            />
            <p className="text-[10px] text-gray-500 text-right">{(r.body || '').length}/400</p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
          Footer note (optional)
        </label>
        <input
          value={doc?.footer || ''}
          onChange={(e) => setField('footer', e.target.value)}
          placeholder="e.g. Questions? Tap support at the bottom of any page."
          maxLength={300}
          className="w-full px-3 py-2.5 rounded-lg text-sm text-white"
          style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="cashout-rules-footer-input"
        />
      </div>

      {/* Save bar — sticks to bottom on mobile */}
      <div
        className="sticky bottom-0 -mx-6 px-6 py-4 flex items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(180deg, rgba(11,14,20,0.4), rgba(11,14,20,0.95))',
          backdropFilter: 'blur(8px)',
        }}
      >
        <span className="text-xs text-gray-400">
          {dirty ? <span className="text-amber-300 font-semibold">Unsaved changes</span> : 'All changes saved'}
        </span>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
            color: '#000',
          }}
          data-testid="cashout-rules-save-btn"
        >
          {saving ? <div className="spinner w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* Live preview using the EXACT player popup, with the
          in-memory unsaved doc so admins see what they've typed. */}
      {preview && (
        <CashoutRulesPopup
          gameId={null}
          overrideDoc={doc}
          onClose={() => setPreview(false)}
        />
      )}
    </div>
  );
}
